import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map, of } from 'rxjs';

import { isNil } from '@gfe/core/utils/shared-utils';

import {
  AccessorMetadata,
  BaseEntityMetadata,
  EntityMetadata
} from '@core/models/entity/entity-metadata.model';
import { GbeEndPointV4 } from '@core/models/gbe/gbe-endpoint.enum';
import { GBE_BASE_URL } from '@core/tokens/gbe-url.token';
import { toCamelCase } from '@core/utils/case-conversions';

@Injectable({
  providedIn: 'root'
})
export class EntitiesService {
  private readonly gbeUrl = inject(GBE_BASE_URL);
  private readonly http = inject(HttpClient);

  /**
   * Fetches the metadata of an entity by its name.
   *
   * @param {number | undefined} dataSpaceId - ID of the data space to fetch entities from.
   */
  public getEntityMetadata$(dataSpaceId?: number): Observable<EntityMetadata[]> {
    const url = `${this.buildUrl(GbeEndPointV4.ENTITIES)}`;
    return this.http
      .get<BaseEntityMetadata[]>(url, {
        params: {
          ...(dataSpaceId ? { dataSpaceId: dataSpaceId.toString() } : {})
        }
      })
      .pipe(
        map((baseMetadatas: BaseEntityMetadata[]) => {
          return baseMetadatas.map((baseMetadata: BaseEntityMetadata) => {
            const versions = baseMetadata.versions.map((versionName) => {
              return {
                versionName,
                entityType: baseMetadata.name.toLowerCase()
              };
            });
            return {
              name: baseMetadata.name.toLowerCase(),
              displayName: baseMetadata.displayName,
              displayNameSingular: baseMetadata.displayNameSingular,
              description: baseMetadata.description,
              keywords: baseMetadata.keywords,
              totalCount: baseMetadata.totalCount,
              extras: baseMetadata.extras ?? {},
              versions
            };
          });
        })
      );
  }

  public deleteMetadata$(metadataId: string): Observable<void> {
    const url = `${this.buildUrl(GbeEndPointV4.ENTITIES)}/${metadataId}/table-metadata`;
    console.log(`Deleting metadata at URL: ${url}`);
    return of();
  }

  private buildUrl(endpoint: string): string {
    return `${this.gbeUrl}/${endpoint}`;
  }

  private transformAccessorKeys<T>(obj: T): T {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => this.transformAccessorKeys(item)) as unknown as T;
    }

    if (typeof obj !== 'object') {
      return obj;
    }

    // Type guard to check if object is AccessorMetadata
    if (this.isAccessorMetadata(obj)) {
      return {
        ...obj,
        key: toCamelCase(obj.key)
      } as T;
    }

    // Recursively transform all object properties
    const result = {} as T;
    for (const [key, value] of Object.entries(obj)) {
      result[key as keyof T] = this.transformAccessorKeys(value);
    }

    return result;
  }

  private isAccessorMetadata(obj: unknown): obj is AccessorMetadata {
    return (
      !isNil(obj) && typeof obj === 'object' && 'key' in obj && typeof obj.key === 'string'
    );
  }
}
